import React from 'react';
import { Info } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from './ui/popover';

/**
 * 락업 물량 표시 (2026-09-17)
 *
 * 왜 필요했나
 *  · 락업 지급은 토큰이 유저 지갑이 아니라 금고 컨트랙트로 간다. 그래서
 *    거래내역에도 안 뜨고, 잔액에는 조용히 합산만 돼 있었다.
 *    20 USDT 를 내고도 화면에 아무 변화가 없어 "안 들어왔다"는 문의가 나왔다.
 *
 * 원칙
 *  · 평소엔 칩 한 줄만. 자세한 건 ⓘ 를 눌렀을 때만 띄운다(화면을 어지럽히지 않는다).
 */
const n = (v: number, d = 0) =>
  Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: d });

export function LockChip({
  locked, available, schedule, symbol = 'wHANDY', tone = 'light',
}: {
  locked: number;
  available: number;
  schedule: { date: string; amount: number }[];
  symbol?: string;
  /** light = 흰 배경용, onDark = 파란 카드 위용 */
  tone?: 'light' | 'onDark';
}) {
  if (!(locked > 0)) return null;

  const MAX = 6;
  const shown = schedule.slice(0, MAX);
  const rest = schedule.length - shown.length;

  const chip = tone === 'onDark'
    ? { background: 'rgba(255,255,255,.18)', color: '#fff' }
    : { background: '#FEF3C7', color: '#B45309' };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={e => e.stopPropagation()}
          className="text-[11px] font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1 leading-none"
          style={chip}
        >
          🔒 락업 {n(locked)}
          <Info className="w-3 h-3" />
        </button>
      </PopoverTrigger>

      <PopoverContent align="start" className="w-64 p-3" onClick={e => e.stopPropagation()}>
        <div className="text-sm font-bold text-foreground mb-2">보유 내역</div>

        <div className="flex justify-between text-sm py-0.5">
          <span className="text-muted-foreground">지금 사용 가능</span>
          <span className="font-semibold text-foreground">{n(available, 2)}</span>
        </div>
        <div className="flex justify-between text-sm py-0.5">
          <span className="text-muted-foreground">락업 중</span>
          <span className="font-semibold" style={{ color: '#B45309' }}>{n(locked, 2)}</span>
        </div>

        {schedule.length > 0 && (
          <>
            <div className="border-t border-border my-2" />
            <div className="text-xs font-bold text-foreground mb-1">해제 일정</div>
            {shown.map(r => (
              <div key={r.date} className="flex justify-between text-xs py-0.5">
                <span className="text-muted-foreground">{r.date}</span>
                <span className="font-medium text-foreground">{n(r.amount)}</span>
              </div>
            ))}
            {rest > 0 && (
              <div className="text-xs text-muted-foreground pt-1">외 {rest}회</div>
            )}
          </>
        )}

        <div className="text-[11px] text-muted-foreground mt-2 leading-snug">
          락업 물량은 금고에 보관되며, 해제일부터 출금할 수 있습니다.
          거래내역에는 해제될 때 표시됩니다.
        </div>
      </PopoverContent>
    </Popover>
  );
}
